import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import { useResetPassword } from "../api/roster.mutations";
import { CopyField } from "./CopyField";

// The temp password lives only in component state, never persisted.
export function ResetPasswordDialog({
  userId,
  name,
}: {
  userId: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const resetPassword = useResetPassword();
  const { showApiError } = useErrorDialog();

  useResetOnOpen(open, () => setTempPassword(null));

  const onConfirm = () =>
    resetPassword.mutate(userId, {
      onSuccess: (data) => setTempPassword(data.temporaryPassword),
      onError: (e) =>
        showApiError(e, { title: "Kunde inte återställa lösenord" }),
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
        >
          <KeyRound className="h-4 w-4" />
          Återställ
        </Button>
      </DialogTrigger>
      <DialogContent>
        {tempPassword === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Återställ lösenord för {name}?</DialogTitle>
              <DialogDescription>
                Ett nytt lösenord kommer skapas för {name} och alla enheter
                kommer loggas ut.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={resetPassword.isPending}
              >
                Avbryt
              </Button>
              <Button onClick={onConfirm} loading={resetPassword.isPending}>
                <KeyRound />
                Återställ lösenord
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Nytt lösenord</DialogTitle>
              <DialogDescription>
                Kopiera lösenordet, det kan inte visas igen.
              </DialogDescription>
            </DialogHeader>
            <CopyField
              label="Lösenord"
              value={tempPassword}
              copyLabel="Kopiera lösenord"
              copiedLabel="Kopierat"
              monospace
            />
            <DialogFooter>
              <Button onClick={() => setOpen(false)}>Klar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
